import type { StudyDocumentDetail, StudySession } from '@/pages/learn/study/studyApi';
import { SAMPLE_IDS } from './sampleProfile';
import { samplePageImage } from './sampleSourceAssets';

/** Authored history in the real Session shape. No model, receipt or new conversation is simulated. */
export async function sampleExplanation(
  document: StudyDocumentDetail,
  termId: string,
  stamp: string,
): Promise<StudySession> {
  const page = await samplePageImage(0);
  return {
    id: SAMPLE_IDS.explanation,
    goal: 'understand',
    status: 'completed',
    highest_hint_level: 1,
    help_policy: { h4_allowed: false },
    summary: null,
    review_due_at: null,
    completed_at: stamp,
    created_at: stamp,
    term: { id: termId, term_number: 4, label: 'Term 4' },
    subject: { subject_key: 'mathematics', display_name: 'Mathematics' },
    document: {
      ...document,
      tutor_source_ready: true,
      selected_page: {
        ...page,
        page_number: 1,
        provenance: 'client_pdfjs',
        read_url_expires_in: 3600,
      },
      focused_region: null,
    },
    learning: {
      id: 'example-learning-explanation',
      started_at: stamp,
      ended_at: stamp,
      stars_used: 0,
      llm_calls: 0,
    },
    attempt: {
      content: 'I thought 3/4 meant three slices, but I need to count equal groups first.',
      submitted_at: stamp,
      updated_at: stamp,
    },
    source_confirmation: null,
    generated_practice: null,
    analysis_question: { analysis_id: SAMPLE_IDS.analysis, question_id: 'example-pizza-source' },
    messages: [
      {
        id: 'example-explanation-question',
        role: 'user',
        content: 'The pizza has eight equal slices. Why does 3/4 not just mean three slices?',
        stars_charged: 0,
        created_at: stamp,
      },
      {
        id: 'example-explanation-reply',
        role: 'assistant',
        stars_charged: 0,
        created_at: stamp,
        content:
          'Pre-generated example explanation — not a live AI reply.\n\nStart with the whole pizza. The denominator 4 asks us to divide that whole into four equal groups. It does not tell us that there are four slices.\n\nEight equal slices can be organised into four groups of two slices. One of those groups is one quarter. The numerator 3 asks for three of those equal groups. Draw the groups before counting how many slices they contain.\n\nCompare three quarters with one half: which covers more of the same whole? Explain your prediction in your own words.\n\nThe Fractions Lab can help you see the same fraction as equal parts, a decimal and a percentage. Open the tool suggestion below, choose four total equal parts and three selected parts, then compare it with one half. The tool will not import this worksheet or choose the values for you. Return here to explain what you noticed.',
      },
    ],
  };
}
